/**
 * Выбор тарифа: три карточки из макета `billing-design-v2`.
 *
 * ★ Ни одной цены и ни одного названия в этом файле. Они приходят с
 * сервера вместе с готовыми строками — в макете, например, Ultimate стоит
 * $899, а утверждённая цена $999. Зашитая в разметку цифра разошлась бы с
 * тем, за что клиента списывают, и заметил бы это он.
 *
 * Перечень возможностей — единственное, что живёт здесь: это маркетинговый
 * текст, он не участвует в расчётах и разойтись ему не с чем.
 */

import { Badge } from "@/components/Badge";
import { Btn } from "@/components/Btn";
import { I } from "@/components/Icon";

export type PlanOption = {
  key: string;
  label: string;
  price_text: string;
};

// Что входит в тариф. Порядок и состав — из макета.
const FEATURES: Record<string, string[]> = {
  starter: [
    "1 Apple Ads account",
    "Automation rules",
    "Daily reporting",
    "Email support",
  ],
  professional: [
    "Up to 5 Apple Ads accounts",
    "Autopilot & suggestions",
    "MMP integrations",
    "Custom metrics",
    "Priority support",
  ],
  professional_ultimate: [
    "Unlimited Apple Ads accounts",
    "Custom commission ladder",
    "Dedicated success manager",
    "SLA & onboarding",
  ],
};

const POPULAR = "professional";

export function PlanPicker({
  plans,
  current,
  busy,
  actionLabel,
  onPick,
  onClose,
}: {
  plans: PlanOption[];
  current: string | null;
  busy: boolean;
  actionLabel: string;
  onPick: (plan: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="card card-pad" style={{ marginBottom: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 4,
        }}
      >
        <div style={{ fontSize: 13.5, fontWeight: 600 }}>Choose a plan</div>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: 0,
            padding: 0,
            cursor: "pointer",
            fontSize: 12.5,
            color: "var(--c-text-3)",
          }}
        >
          Cancel
        </button>
      </div>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 16 }}>
        The fixed fee is prorated from today; the spend commission is charged
        at the next renewal.
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 14,
        }}
      >
        {plans.map((p) => {
          const isCurrent = p.key === current;
          const popular = p.key === POPULAR;
          return (
            <div
              key={p.key}
              className="card card-pad"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 14,
                border: popular
                  ? "1px solid var(--c-accent-200)"
                  : "1px solid var(--c-border)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  minHeight: 24,
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 600 }}>{p.label}</div>
                {isCurrent ? (
                  <Badge kind="paused">Current plan</Badge>
                ) : popular ? (
                  <Badge kind="accent">Most popular</Badge>
                ) : null}
              </div>

              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 24,
                  fontWeight: 500,
                  letterSpacing: "-0.01em",
                }}
              >
                {p.price_text}
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  flex: 1,
                }}
              >
                {(FEATURES[p.key] ?? []).map((f) => (
                  <div
                    key={f}
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "flex-start",
                      fontSize: 13,
                      color: "var(--c-text-2)",
                    }}
                  >
                    <span
                      style={{
                        color: "var(--c-pos-500)",
                        flex: "0 0 auto",
                        display: "inline-flex",
                        marginTop: 1,
                      }}
                    >
                      {I.check}
                    </span>
                    {f}
                  </div>
                ))}
              </div>

              <Btn
                variant={isCurrent ? "default" : "primary"}
                disabled={isCurrent || busy}
                onClick={() => onPick(p.key)}
                style={{ justifyContent: "center", width: "100%" }}
              >
                {isCurrent ? "Current plan" : actionLabel}
              </Btn>
            </div>
          );
        })}
      </div>
    </div>
  );
}
